/**
 * The type a WBS / sub-project is created as, shared by every picker in the
 * app so the list can't drift between them.
 *
 * The `value` is what gets stored on Project.type - a free-form varchar on the
 * backend (see project.entity.ts), so nothing server-side validates or
 * migrates it. That cuts both ways: new codes save fine, and every code ever
 * saved before is still sitting in the database. Hence LEGACY_PROJECT_TYPES:
 * old records keep resolving to the right discipline, they're just no longer
 * offered when creating something new.
 *
 * Read a stored type through projectTypeDiscipline() rather than comparing it
 * to a literal - that's what keeps both generations of codes working.
 */
export const PROJECT_TYPES = [
  { value: "PVE", label: "Estimation - Pressure Vessel", discipline: "pv" },
  { value: "TAE", label: "Estimation - Storage Tank", discipline: "tank" },
  { value: "PIE", label: "Estimation - Piping", discipline: "pip" },
  { value: "INE", label: "Estimation - Instrumentation", discipline: "ins" },
  { value: "STE", label: "Estimation - Structural", discipline: "struc" },
  { value: "PLE", label: "Estimation - Plant", discipline: "plant" },
  { value: "spec", label: "Spec", discipline: "spec" },
];

// Codes from before the PVE/TAE/... scheme, still carried by existing records.
// "ga" has no counterpart in the new list, so it stays a discipline of its own
// rather than being folded into another one or silently dropped.
export const LEGACY_PROJECT_TYPES = [
  { value: "pv", label: "Pressure Vessel", discipline: "pv" },
  { value: "pip", label: "Piping", discipline: "pip" },
  { value: "struc", label: "Structural", discipline: "struc" },
  { value: "ga", label: "GA", discipline: "ga" },
];

// What a discipline is called on screen where the full "Estimation - …" type
// name would be too long, e.g. the tree's grouping folders. Order here is the
// order those groups appear in.
export const PROJECT_DISCIPLINES = [
  { key: "pv", label: "Pressure Vessel" },
  { key: "tank", label: "Storage Tank" },
  { key: "pip", label: "Piping" },
  { key: "ins", label: "Instrumentation" },
  { key: "struc", label: "Structural" },
  { key: "plant", label: "Plant" },
  { key: "spec", label: "Spec" },
  { key: "ga", label: "GA" },
];

const ALL_PROJECT_TYPES = [...PROJECT_TYPES, ...LEGACY_PROJECT_TYPES];

const normalizeType = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase();

const findProjectType = (value) => {
  const normalized = normalizeType(value);
  if (!normalized) return null;
  return (
    ALL_PROJECT_TYPES.find(
      (type) => type.value.toLowerCase() === normalized,
    ) || null
  );
};

/** Discipline a stored type belongs to ("pv", "pip", …), or null if unknown. */
export const projectTypeDiscipline = (value) =>
  findProjectType(value)?.discipline ?? null;

/** Display name for a stored type, falling back to the raw value. */
export const projectTypeLabel = (value) =>
  findProjectType(value)?.label || value || "-";

/**
 * The type a new WBS in this discipline is created as - null for a discipline
 * that has no current code to create against ("ga", which only exists as a
 * legacy type). That null is what tells the UI a discipline can be shown but
 * not added to.
 */
export const disciplineDefaultType = (key) =>
  PROJECT_TYPES.find((type) => type.discipline === key)?.value ?? null;

/**
 * Short type code for a discipline ("PVE", "PIE", …), used where the full
 * discipline name is too wide - the tree's grouping folders. Disciplines that
 * only ever had a legacy code, like "ga", fall back to the key itself.
 */
export const disciplineCode = (key) =>
  (disciplineDefaultType(key) || key).toUpperCase();

/** Display name for a discipline key. */
export const disciplineLabel = (key) =>
  PROJECT_DISCIPLINES.find((discipline) => discipline.key === key)?.label ||
  key;

/** True when a stored type belongs to any of the given disciplines. */
export const isProjectTypeIn = (value, disciplines) =>
  disciplines.includes(projectTypeDiscipline(value));

// ─── Piping: the Spec / Estimation WBS split ────────────────────────────────
// The Piping module used to be reached through a single "pip" WBS carrying two
// tabs, Spec and Estimation. Those halves are now WBS in their own right - one
// "spec", one "PIE" - so each can be planned, assigned and tracked separately.
//
// Each WBS is its own record with its own revision blob, matching the Software
// (docs/plans/piping-module-split.md there), which is where most Spec data is
// written. The Spec record owns the plant's specs - selectedSpecs,
// customSpecRows, specHelperConfigs - and an Estimation WBS reads them from it
// on every load (see Ams/Piping/specSourceLink.js) rather than keeping its own.
//
// Legacy "pip" WBS predate the split, own both halves in one blob, and still
// show both tabs themselves.

/**
 * Types a plant may hold only one of. Spec is the plant's single spec source,
 * so a second one would leave every Estimation WBS unable to tell which to
 * read - the Software enforces the same rule (SINGLETON_TYPES).
 */
export const SINGLETON_PROJECT_TYPES = ["spec"];

/** True when a plant may hold only one WBS of this stored type. */
export const isSingletonProjectType = (value) =>
  SINGLETON_PROJECT_TYPES.includes(normalizeType(value));

/** Piping tabs a WBS of this stored type opens. Empty for a non-piping type. */
export const pipingWbsTabs = (value) => {
  const type = normalizeType(value);
  if (type === "spec") return ["spec"];
  if (type === "pie") return ["estimation"];
  // Legacy "pip", and anything else resolving to the piping discipline.
  if (projectTypeDiscipline(value) === "pip") return ["spec", "estimation"];
  return [];
};

/**
 * True for a WBS that reads its specs from the plant's Spec record instead of
 * owning them: the split-out Estimation WBS. Not the Spec record itself (it is
 * the source), and not legacy "pip", which has always kept its own specs.
 */
export const pipingReadsLinkedSpec = (value) => {
  const tabs = pipingWbsTabs(value);
  return tabs.includes("estimation") && !tabs.includes("spec");
};
